import { useParams } from "react-router-dom";
import classNames from "classnames";

import {
  availableTDS,
  PaymentMethods,
  PaymentMethodUILabel,
} from "@graffer-inc/cloud-gov-core";
import { PrimaryButton } from "@graffer-inc/console-design-kit";
import OutsideClickHandler from "@graffer-inc/react-outside-click-handler";

import { ServiceProviderTemplate } from "../../../../template/service-provider/default-template";
import { ServiceProviderR } from "../../routes";
import { ServiceProviderTabs } from "../../../../molecules/service-provider-tab";
import { Dropdown } from "../../../../molecules/dropdown";
import { BaseModal } from "../../../../atoms/base-modal";
import { ConfirmModal } from "../../../../organisms/common/confirm-modal";
import { QualifiedInvoiceBusinessIssuersIndex } from "../../qualified-invoice/index";
import { useServiceProviderOrgPaymentSettingHooks } from "./hooks";
import { ServiceProviderPayment3DSecureAuthRawdata } from "./payment-rawdata/3dsecure-auth";
import { ServiceProviderPaymentRawdata } from "./payment-rawdata/payment-rawdata";
import dayjs, { formatDatetime } from "../../../../../utils/date-utils";
import ReactDatePicker from "react-datepicker";

export const ServiceProviderOrgPaymentSettingPage = () => {
  const { id: orgId } = useParams<{ id: string }>();

  const {
    isLoading,
    merchantId,
    serviceId,
    paymentSetting,
    availablePaymentMethods,
    tdsAvailableAt,
    createPaymentSettingModal,
    deletePaymentSettingModal,
    paymentSettingMenuToggle,
    setMerchantId,
    setServiceId,
    setAvailablePaymentMethods,
    setTdsAvailableAt,
    onCreatePaymentSettingModalClose,
    onSubmitPaymentSetting,
    onDeletePaymentSetting,
  } = useServiceProviderOrgPaymentSettingHooks({
    orgId,
  });

  return (
    <ServiceProviderTemplate
      breadcrumbs={[{ to: ServiceProviderR.Home.link({}), title: "Home" }]}
    >
      <div className="has-text-left">
        <div className="columns is-centered">
          <div className="column">
            <ServiceProviderTabs orgId={orgId} />
            {/* 決済データ */}
            <div className="column mb-5">
              <h3
                className="title is-size-4 mt-4 mb-4 pl-2"
                style={{
                  borderLeft: "6px solid #40bfb0",
                }}
              >
                決済データ
              </h3>
              <div className="mt-5 mb-5">
                <h2 className="subtitle">決済データ</h2>
                <ServiceProviderPaymentRawdata orgId={orgId} />
              </div>
              {availableTDS(paymentSetting, true) && (
                <div className="pt-5">
                  <h2 className="subtitle">3-Dセキュア認証データ</h2>
                  <ServiceProviderPayment3DSecureAuthRawdata orgId={orgId} />
                </div>
              )}
            </div>
            {/* SBPS接続情報 */}
            <div className="column">
              <h3
                className="title is-size-4 mt-4 mb-4 pl-2"
                style={{
                  borderLeft: "6px solid #40bfb0",
                }}
              >
                SBPS接続情報
              </h3>
              <nav className="level">
                <div className="level-left" />
                <div className="level-right">
                  <p className="level-item">
                    <PrimaryButton
                      text={paymentSetting ? "更新" : "新規登録"}
                      onClick={createPaymentSettingModal.open}
                    />
                  </p>
                  {paymentSetting && (
                    <OutsideClickHandler
                      onOutsideClick={paymentSettingMenuToggle.close}
                    >
                      <Dropdown
                        onClick={paymentSettingMenuToggle.open}
                        isActive={paymentSettingMenuToggle.isActive}
                        isRight={true}
                        menus={[
                          {
                            title: "SBPS登録情報を削除",
                            onClick: deletePaymentSettingModal.open,
                          },
                        ]}
                      />
                    </OutsideClickHandler>
                  )}
                </div>
              </nav>
              <div className="field">
                <div className="label">マーチャントID</div>
                <div className="control">
                  <p>
                    {paymentSetting
                      ? paymentSetting.sbpsMerchantId
                      : "設定なし"}
                  </p>
                </div>
              </div>
              <div className="field">
                <div className="label">サービスID</div>
                <div className="control">
                  <p>
                    {paymentSetting ? paymentSetting.sbpsServiceId : "設定なし"}
                  </p>
                </div>
              </div>
              <div className="field">
                <div className="label">決済手段</div>
                {PaymentMethods.map((method) => (
                  <div className="control m-3">
                    <input
                      type="checkbox"
                      className="switch is-rounded"
                      checked={
                        paymentSetting && paymentSetting.availablePaymentMethods
                          ? paymentSetting.availablePaymentMethods.includes(
                              method,
                            )
                          : false
                      }
                      readOnly
                    />
                    <label>{PaymentMethodUILabel[method]}</label>
                  </div>
                ))}
              </div>
            </div>
            <div className="field">
              <div className="label">3-Dセキュア利用開始日時</div>
              <div className="control">
                <p>
                  {paymentSetting?.tdsAvailableAt
                    ? formatDatetime(paymentSetting.tdsAvailableAt)
                    : "設定なし"}
                </p>
              </div>
            </div>
          </div>
          <BaseModal
            isActive={createPaymentSettingModal.isActive}
            title={
              paymentSetting
                ? "SBPS設定情報を更新する"
                : "SBPS設定情報を登録する"
            }
            onClose={onCreatePaymentSettingModalClose}
            hasOverflow={true}
          >
            <section className="modal-card-body">
              <div className="field">
                SBPSから送付された接続情報を入力してください。
              </div>
              <div className="field">
                <div className="label">マーチャントID</div>
                <div className="control">
                  <input
                    className="input"
                    value={merchantId}
                    onChange={(e) => setMerchantId(e.target.value.trim())}
                  />
                </div>
              </div>
              <div className="field">
                <div className="label">サービスID</div>
                <div className="control">
                  <input
                    className="input"
                    value={serviceId}
                    onChange={(e) => setServiceId(e.target.value.trim())}
                  />
                </div>
              </div>
              <div className="field">
                <div className="label">決済手段</div>
                <button
                  className="button has-text-left is-white"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <input
                    type="checkbox"
                    className="switch is-rounded"
                    checked={true}
                    disabled
                  />
                  <label>クレジットカード</label>
                </button>
              </div>
              <div className="field">
                <button
                  className="button has-text-left is-white"
                  onClick={(e) => {
                    e.stopPropagation();
                    setAvailablePaymentMethods((v) => {
                      return v.includes("paypay")
                        ? [...v.filter((v) => v !== "paypay")]
                        : [...v, "paypay"];
                    });
                  }}
                >
                  <input
                    type="checkbox"
                    className="switch is-rounded"
                    checked={availablePaymentMethods.includes("paypay")}
                  />
                  <label>PayPay</label>
                </button>
              </div>
              <div className="field">
                <button
                  className="button has-text-left is-white"
                  onClick={(e) => {
                    e.stopPropagation();
                    setAvailablePaymentMethods((v) => {
                      return v.includes("payeasy")
                        ? [...v.filter((v) => v !== "payeasy")]
                        : [...v, "payeasy"];
                    });
                  }}
                >
                  <input
                    type="checkbox"
                    className="switch is-rounded"
                    checked={availablePaymentMethods.includes("payeasy")}
                  />
                  <label>ペイジー</label>
                </button>
              </div>
              <div className="field">
                <div className="label">3-Dセキュア利用開始日時</div>
                <p>
                  設定日時以降、クレジットカード決済時に本人認証が行われるようになります。
                </p>
                <div className="control" style={{ padding: "10px 0 0 0" }}>
                  <ReactDatePicker
                    className="input"
                    selected={
                      tdsAvailableAt ? dayjs(tdsAvailableAt).toDate() : null
                    }
                    placeholderText="----/--/-- --:--"
                    onChange={(date) => {
                      setTdsAvailableAt(date);
                    }}
                    showTimeSelect
                    timeFormat="HH:mm"
                    timeIntervals={1}
                    timeCaption="時分"
                    dateFormat="yyyy/MM/dd HH:mm"
                  />
                </div>
              </div>
            </section>
            <footer className="modal-card-foot">
              <button
                className="button is-failure is-fullwidth"
                onClick={onCreatePaymentSettingModalClose}
              >
                キャンセル
              </button>
              <button
                className={classNames("button is-info is-fullwidth", {
                  "is-loading": isLoading,
                })}
                disabled={!merchantId || !serviceId}
                onClick={onSubmitPaymentSetting}
              >
                送信する
              </button>
            </footer>
          </BaseModal>
          <ConfirmModal
            isActive={deletePaymentSettingModal.isActive}
            title={"SBPS接続情報の削除"}
            body={
              "登録されているSBPS接続情報を削除します。よろしいでしょうか。"
            }
            successButtonText={"削除する"}
            onSuccess={() => {
              onDeletePaymentSetting(orgId);
              deletePaymentSettingModal.close();
            }}
            onClose={deletePaymentSettingModal.close}
          />
        </div>
        {/* 適格請求書発行事業者 */}
        <QualifiedInvoiceBusinessIssuersIndex />
      </div>
    </ServiceProviderTemplate>
  );
};
